/**
 * <product-rail> — peek-edge product rail. Figma 70:137.
 *
 * A custom element, not an inline <script>: the Section Rendering API re-injects section HTML on
 * every theme-editor change and never executes re-inserted script tags, but custom elements upgrade
 * automatically on insertion. Same reason the press marquee is CSS-only.
 *
 * Scrolling itself is native (overflow-x + scroll-snap), so touch, trackpad, keyboard and
 * scroll-into-view all work with no JS. This class only drives the arrows and the progress bar.
 */
// Wrapped in an IIFE because a section can appear twice on one page (rail.liquid does),
// which emits this script twice; a second top-level `class` declaration is a hard error.
(() => {
class ProductRail extends HTMLElement {
  connectedCallback() {
    this.rail = this.querySelector('[data-rail]');
    this.thumb = this.querySelector('[data-rail-thumb]');
    this.prev = this.querySelector('[data-rail-prev]');
    this.next = this.querySelector('[data-rail-next]');
    if (!this.rail) return;

    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.onScroll = this.onScroll.bind(this);

    this.rail.addEventListener('scroll', this.onScroll, { passive: true });
    this.prev?.addEventListener('click', () => this.step(-1));
    this.next?.addEventListener('click', () => this.step(1));

    // ResizeObserver keeps the thumb honest when the card count or viewport changes.
    this.ro = new ResizeObserver(() => this.onScroll());
    this.ro.observe(this.rail);
    this.onScroll();
  }

  disconnectedCallback() {
    this.rail?.removeEventListener('scroll', this.onScroll);
    this.ro?.disconnect();
  }

  get cardWidth() {
    const card = this.rail.querySelector('[data-rail-card]');
    if (!card) return this.rail.clientWidth;
    const gap = parseFloat(getComputedStyle(this.rail).columnGap) || 0;
    return card.getBoundingClientRect().width + gap;
  }

  step(dir) {
    this.rail.scrollBy({
      left: dir * this.cardWidth,
      behavior: this.reduced.matches ? 'auto' : 'smooth',
    });
  }

  onScroll() {
    // Coalesce bursts of scroll events into one rAF so the layout read + style writes below don't
    // thrash on every event.
    if (this._rafPending) return;
    this._rafPending = true;
    requestAnimationFrame(() => { this._rafPending = false; this.measure(); });
  }

  measure() {
    const max = this.rail.scrollWidth - this.rail.clientWidth;
    const visible = this.rail.clientWidth / this.rail.scrollWidth;

    if (this.thumb) {
      // Thumb length mirrors how much of the rail is on screen; position mirrors scroll progress.
      const pct = max > 0 ? this.rail.scrollLeft / max : 0;
      this.thumb.style.setProperty('--rail-thumb-size', `${Math.min(visible, 1) * 100}%`);
      this.thumb.style.setProperty('--rail-thumb-pos', `${pct * (100 - Math.min(visible, 1) * 100)}%`);
    }

    // Disable the arrow that would do nothing. A disabled control is honest; a dead one is not.
    const atStart = this.rail.scrollLeft <= 1;
    const atEnd = this.rail.scrollLeft >= max - 1;
    if (this.prev) this.prev.disabled = atStart;
    if (this.next) this.next.disabled = atEnd || max <= 0;
    this.toggleAttribute('data-scrollable', max > 0);
  }
}

if (!customElements.get('product-rail')) customElements.define('product-rail', ProductRail);
})();
